import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import ModeSelector from '../ModeSelector';
import NotificationDropdown from '../NotificationDropdown';

const DJHeader = ({
  event,
  eventId,
  activePart,
  playerState,
  pendingSuggestions,
  isFullscreen,
  showQRCode,
  onModeChange,
  onToggleQRCode,
  onToggleFullscreen,
  onSuggestionAction
}) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            data-testid="back-to-dashboard-button"
            onClick={() => navigate('/dashboard')}
            variant="outline"
            size="icon"
            className="border-sunset-orange/30 text-sunset-orange hover:bg-sunset-orange/10 hover:border-sunset-orange/50 hover:text-text-primary min-w-[44px] min-h-[44px]"
            aria-label="Retour au tableau de bord"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 
                className="text-lg sm:text-2xl font-bold text-white truncate max-w-[200px] sm:max-w-none" 
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                {event?.name}
              </h1>
              {event?.status === 'live' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                  🔴 EN DIRECT
                </span>
              )}
              {event?.status === 'draft' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-sunset-orange border border-sunset-orange/30">
                  📝 Brouillon
                </span>
              )}
            </div>
            <p className="text-xs text-sunset-orange/70 mt-1">
              Interface DJ Live
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Manage Event Button */}
          <Button
            onClick={() => window.open(`/events/${eventId}`, '_blank')}
            variant="outline"
            size="icon"
            className="border-sunset-orange/30 text-sunset-orange hover:bg-sunset-orange/10 hover:border-sunset-orange/50 hover:text-text-primary"
            title="Gérer l'événement"
          >
            <Edit className="w-5 h-5" />
          </Button>

          {/* Logout Button */}
          <Button
            onClick={handleLogout}
            variant="outline"
            size="icon"
            className="border-sunset-orange/30 text-sunset-orange hover:bg-sunset-orange/10 hover:border-sunset-orange/50 hover:text-text-primary"
            title="Déconnexion"
          >
            <LogOut className="w-5 h-5" />
          </Button>
          
          {/* Mode Selector */}
          {activePart && (
            <ModeSelector
              currentMode={playerState?.mode || activePart.mode || 'auto'}
              onModeChange={onModeChange}
              disabled={!playerState?.current_part_id}
            />
          )}
          
          {/* QR Code / Guest Link */}
          <Button
            onClick={onToggleQRCode}
            variant="outline"
            size="icon"
            className="border-sunset-orange/30 text-sunset-orange hover:bg-sunset-orange/10 hover:border-sunset-orange/50 hover:text-text-primary relative"
            title="Lien invités (QR Code)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="3" width="8" height="8" strokeWidth="2" />
              <rect x="3" y="13" width="8" height="8" strokeWidth="2" />
              <rect x="13" y="3" width="8" height="8" strokeWidth="2" />
              <path d="M13 13h2v2h-2zm0 4h2v2h-2zm4-4h2v2h-2zm0 4h2v2h-2zm2-2h2v2h-2z" strokeWidth="2" />
            </svg>
          </Button>

          {/* Fullscreen Toggle */}
          <Button
            onClick={onToggleFullscreen}
            variant="outline"
            size="icon"
            className="border-sunset-orange/30 text-sunset-orange hover:bg-sunset-orange/10 hover:border-sunset-orange/50 hover:text-text-primary"
            title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          >
            {isFullscreen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </Button>
          
          {/* Notification Dropdown */}
          <NotificationDropdown
            suggestions={pendingSuggestions}
            onAccept={(id) => onSuggestionAction(id, 'accepted')}
            onReject={(id) => onSuggestionAction(id, 'rejected')}
          />
        </div>
      </div>
    </header>
  );
};

export default DJHeader;
